import React from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { Typography } from '../interfaces/typography'
import { SubscriptionType } from '../interfaces/users'
import { EXTERNAL_LINKS, ACCESS_CONTROL_MESSAGES } from './constants'
import { useUserData } from '../lib/redux/users/user-data-hook'

interface AccessControlProps {
  children: React.ReactNode
}

// Overlay component for access denied states
const AccessDeniedOverlay: React.FC<{
  title: string
  message: string
  actionButton?: {
    text: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }
  iconType?: 'upgrade' | 'admin'
}> = ({ title, message, actionButton, iconType = 'upgrade' }) => (
  <div className="relative h-full">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-blue-50/30 backdrop-blur-sm z-10 flex items-center justify-center">
      <div className="text-center max-w-lg mx-auto p-8">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
          {iconType === 'upgrade' ? (
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ) : (
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 3h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="mb-8">
          <div className="mb-3">
            <Typography variant="h2" color="#1E293B" fontWeight={700}>
              {title}
            </Typography>
          </div>
          <div className="max-w-md mx-auto">
            <Typography variant="largeParagraph" color="#64748B">
              {message}
            </Typography>
          </div>
        </div>
        
        {/* Action Button */}
        {actionButton && (
          <button
            onClick={actionButton.onClick}
            className={`inline-flex items-center px-6 py-3 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              actionButton.variant === 'secondary' 
                ? 'bg-white border border-gray-300 hover:bg-gray-50 focus:ring-gray-300' 
                : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 focus:ring-blue-300'
            }`}
          >
            <Typography 
              variant="mediumParagraph" 
              fontWeight={500}
              color={actionButton.variant === 'secondary' ? '#374151' : '#FFFFFF'}
            >
              {actionButton.text}
            </Typography>
          </button>
        )}
      </div>
    </div>
    <div className="filter blur-sm opacity-30 pointer-events-none h-full overflow-hidden">
      {/* This will show the blurred content behind */}
    </div>
  </div>
)

export const RequiresAdminAccess: React.FC<AccessControlProps> = ({ children }) => {
  const { isAdmin } = useUserData()
  const isPaid = useSelector((state: RootState) => 
    state.user.value?.autodialer_paid || state.team.value?.subscription_type === SubscriptionType.TEAM
  )

  if (!isAdmin && !isPaid) {
    return (
      <AccessDeniedOverlay
        title={ACCESS_CONTROL_MESSAGES.ADMIN_ACCESS.TITLE}
        message={ACCESS_CONTROL_MESSAGES.ADMIN_ACCESS.MESSAGE}
        iconType="admin"
      />
    )
  }

  if (!isAdmin) {
    return (
      <AccessDeniedOverlay 
        title={ACCESS_CONTROL_MESSAGES.ADMIN_ACCESS_PAID.TITLE} 
        message={ACCESS_CONTROL_MESSAGES.ADMIN_ACCESS_PAID.MESSAGE}
        iconType="admin"
      />
    )
  }

  return <>{children}</>
}

export const RequiresPaidAccess: React.FC<AccessControlProps> = ({ children }) => {
  const isPaid = useSelector((state: RootState) => 
    state.user.value?.autodialer_paid || state.team.value?.subscription_type === SubscriptionType.TEAM
  )

  const handleUpgrade = () => {
    window.open(EXTERNAL_LINKS.PRICING_URL, '_blank')
  }

  if (!isPaid) {
    return (
      <AccessDeniedOverlay 
        title={ACCESS_CONTROL_MESSAGES.PAID_ACCESS.TITLE} 
        message={ACCESS_CONTROL_MESSAGES.PAID_ACCESS.MESSAGE}
        iconType="upgrade"
        actionButton={{
          text: ACCESS_CONTROL_MESSAGES.PAID_ACCESS.BUTTON_TEXT,
          onClick: handleUpgrade,
          variant: 'primary'
        }}
      />
    )
  }

  return <>{children}</>
}

export const RequiresParallelAccess: React.FC<AccessControlProps> = ({ children }) => {
  // Assuming parallel access is related to paid features for now
  // You can adjust this logic based on how parallel access is determined
  const hasParallelAccess = useSelector((state: RootState) => state.team.value !== null && state.team.value.telephony_seats !== 0);

  const handleUpgrade = () => {
    window.open(EXTERNAL_LINKS.PARALLEL_UPGRADE_URL, '_blank')
  }

  if (!hasParallelAccess) {
    return (
      <AccessDeniedOverlay 
        title={ACCESS_CONTROL_MESSAGES.PARALLEL_ACCESS.TITLE} 
        message={ACCESS_CONTROL_MESSAGES.PARALLEL_ACCESS.MESSAGE}
        iconType="upgrade"
        actionButton={{
          text: ACCESS_CONTROL_MESSAGES.PARALLEL_ACCESS.BUTTON_TEXT,
          onClick: handleUpgrade,
          variant: 'primary'
        }}
      />
    )
  }

  return <>{children}</>
} 